import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type {
  OrderLifecycleContractGovernanceEvent,
  OrderLifecycleResponse,
  SolanaOrderGovernanceChainState,
} from '@/types'
import type { GovernanceAction } from './orderLifecycleTypes'
import { CopyIconButton } from './orderLifecycleShared'

const ACTIVE_SOLANA_EXECUTION_STATUSES = new Set([
  'enqueueing',
  'queued',
  'ready',
  'executing',
  'retrying',
])

const getSolanaExecutionStatus = (
  event: OrderLifecycleContractGovernanceEvent
): string | undefined => {
  const metadata = event.metadata
  const pauseSequence = metadata?.pauseSequence
  return metadata?.schemaVersion === '2'
    && metadata.scope === 'order'
    && typeof metadata.executionStatus === 'string'
    && typeof metadata.targetPaused === 'boolean'
    && typeof pauseSequence === 'number'
    && Number.isSafeInteger(pauseSequence)
    && pauseSequence >= 1
    ? metadata.executionStatus
    : undefined
}

type SolanaGovernanceAddressRoles = {
  paymentAddress?: string
  vaultTokenAccount?: string
  orderStateAddress?: string
  programAddress?: string
}

const readAddress = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined

export const resolveSolanaGovernanceAddressRoles = (
  event: OrderLifecycleContractGovernanceEvent,
  lifecycle?: OrderLifecycleResponse
): SolanaGovernanceAddressRoles => ({
  paymentAddress: readAddress(lifecycle?.order?.paymentAddress),
  vaultTokenAccount:
    readAddress(event.metadata?.orderVaultAta)
    ?? readAddress(lifecycle?.order?.vaultTokenAccount),
  orderStateAddress:
    readAddress(event.metadata?.orderStatePda)
    ?? readAddress(lifecycle?.order?.orderStatePda),
  programAddress:
    readAddress(event.metadata?.programId)
    ?? readAddress(lifecycle?.order?.programId),
})

export const deriveGovernanceUiState = (
  events: OrderLifecycleContractGovernanceEvent[],
  isSolana: boolean,
  solanaGovernanceState?: SolanaOrderGovernanceChainState
): {
  hasCloseEvent: boolean
  hasPendingSolanaAction: boolean
  isPaused: boolean
} => {
  const hasRecordedCloseEvent = events.some(event => event.action === 'close_contract')
  if (!isSolana) {
    const latestEvent = events.length > 0 ? events[events.length - 1] : undefined
    return {
      hasCloseEvent: hasRecordedCloseEvent,
      hasPendingSolanaAction: false,
      isPaused: !hasRecordedCloseEvent && latestEvent?.action === 'emergency_pause',
    }
  }

  const hasPendingSolanaAction = events.some(event => {
    const executionStatus = getSolanaExecutionStatus(event)
    return executionStatus !== undefined
      && ACTIVE_SOLANA_EXECUTION_STATUSES.has(executionStatus)
  })

  return {
    hasCloseEvent: solanaGovernanceState?.orderClosed === true,
    hasPendingSolanaAction,
    isPaused:
      solanaGovernanceState?.orderStateExists === true
      && solanaGovernanceState.orderClosed !== true
      && solanaGovernanceState.orderPaused === true,
  }
}

const getExecutionStatusColor = (status: string): string => {
  if (status === 'confirmed') return 'green'
  if (status === 'failed' || status === 'superseded') return 'red'
  return 'blue'
}

interface OrderGovernanceSectionProps {
  events?: OrderLifecycleContractGovernanceEvent[]
  lifecycle?: OrderLifecycleResponse
  isSolana: boolean
  solanaOrderPauseSupported?: boolean
  solanaGovernanceState?: SolanaOrderGovernanceChainState
  isSolanaGovernanceStateLoading: boolean
  isSolanaGovernanceStateError: boolean
  governanceNotes: string
  onGovernanceNotesChange: (value: string) => void
  activeGovernanceAction: GovernanceAction | null
  isLoading: boolean
  onRequestAction: (action: GovernanceAction) => void
  getGovernanceActionLabel: (action: GovernanceAction) => string
  getGovernanceActionBadgeColor: (action: string) => string
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  locale?: string
  t: TFunction
}

export const OrderGovernanceSection = ({
  events,
  lifecycle,
  isSolana,
  solanaOrderPauseSupported,
  solanaGovernanceState,
  isSolanaGovernanceStateLoading,
  isSolanaGovernanceStateError,
  governanceNotes,
  onGovernanceNotesChange,
  activeGovernanceAction,
  isLoading,
  onRequestAction,
  getGovernanceActionLabel,
  getGovernanceActionBadgeColor,
  formatTimestamp,
  locale,
  t,
}: OrderGovernanceSectionProps) => {
  const governanceEvents = events ?? []
  const sorted = [...governanceEvents].sort((a, b) => {
    const first = new Date(a.createdAt ?? a.updatedAt ?? 0).getTime()
    const second = new Date(b.createdAt ?? b.updatedAt ?? 0).getTime()
    return first - second
  })

  const hasDeployableContract = (lifecycle?.deployments ?? []).some(deployment => {
    const status = (deployment.status ?? '').toLowerCase()
    const contractAddress = deployment.contractAddress?.trim()
    return status === 'successful' && Boolean(contractAddress)
  })

  const { hasCloseEvent, hasPendingSolanaAction, isPaused } = deriveGovernanceUiState(
    sorted,
    isSolana,
    solanaGovernanceState
  )
  const solanaPauseUnavailable = isSolana && solanaOrderPauseSupported !== true
  const solanaStateUnavailable = isSolana
    && !solanaPauseUnavailable
    && (!solanaGovernanceState || solanaGovernanceState.orderStateExists !== true)
  const solanaProgramPaused = solanaGovernanceState?.programPaused === true
  const canPause = hasDeployableContract
    && !hasCloseEvent
    && !isPaused
    && !hasPendingSolanaAction
    && !solanaPauseUnavailable
    && !solanaStateUnavailable
    && !solanaProgramPaused
  const canResume = hasDeployableContract
    && !hasCloseEvent
    && isPaused
    && !hasPendingSolanaAction
    && !solanaPauseUnavailable
    && !solanaStateUnavailable
    && !solanaProgramPaused
  const canClose = hasDeployableContract && !hasCloseEvent

  const statusKey = !hasDeployableContract
      ? 'unverified'
      : solanaProgramPaused
        ? 'programPaused'
        : solanaStateUnavailable
          ? 'unknown'
          : hasCloseEvent
            ? 'closed'
            : isPaused
              ? 'paused'
              : 'active'
  const statusLabel = t(`orders.lifecycle.governance.currentStatus.${statusKey}`)
  const statusColor = statusKey === 'programPaused' || statusKey === 'closed'
    ? 'red'
    : statusKey === 'unknown' || statusKey === 'unverified'
      ? 'gray'
      : statusKey === 'paused'
        ? 'orange'
        : 'green'
  const governanceDisabledMessage = hasDeployableContract
    ? null
    : t('orders.lifecycle.governance.disabled.contractNotVerified')
  let pauseResumeDisabledMessage = governanceDisabledMessage
  if (!pauseResumeDisabledMessage && solanaPauseUnavailable) {
    pauseResumeDisabledMessage = t('orders.lifecycle.governance.disabled.solanaPauseUnsupported')
  } else if (!pauseResumeDisabledMessage && solanaStateUnavailable) {
    pauseResumeDisabledMessage = t(isSolanaGovernanceStateLoading
      && !isSolanaGovernanceStateError
      ? 'orders.lifecycle.governance.disabled.solanaStateLoading'
      : 'orders.lifecycle.governance.disabled.solanaStateUnavailable')
  } else if (!pauseResumeDisabledMessage && solanaProgramPaused) {
    pauseResumeDisabledMessage = t('orders.lifecycle.governance.disabled.solanaProgramPaused')
  } else if (!pauseResumeDisabledMessage && hasPendingSolanaAction) {
    pauseResumeDisabledMessage = t('orders.lifecycle.governance.disabled.solanaPausePending')
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <VStack align="stretch" spacing={3}>
          <HStack spacing={3} align="center">
            <Text fontSize="sm" fontWeight="medium">
              {t('orders.lifecycle.governance.currentStatus.label')}
            </Text>
            <Badge colorScheme={statusColor}>{statusLabel}</Badge>
          </HStack>
          <Text fontSize="sm" color="gray.600">
            {t('orders.lifecycle.governance.description')}
          </Text>
          <FormControl>
            <FormLabel fontSize="sm">{t('orders.lifecycle.governance.notesLabel')}</FormLabel>
            <Textarea
              size="sm"
              placeholder={t('orders.lifecycle.governance.notesPlaceholder') ?? ''}
              value={governanceNotes}
              onChange={event => onGovernanceNotesChange(event.target.value)}
              resize="vertical"
              rows={3}
            />
            <FormHelperText fontSize="xs">{t('orders.lifecycle.governance.notesHelper')}</FormHelperText>
          </FormControl>
          {!hasDeployableContract && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{governanceDisabledMessage}</Text>
            </Alert>
          )}
          {hasDeployableContract && solanaPauseUnavailable && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{pauseResumeDisabledMessage}</Text>
            </Alert>
          )}
          {hasDeployableContract
            && !solanaPauseUnavailable
            && !solanaStateUnavailable
            && !solanaProgramPaused
            && hasPendingSolanaAction && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{pauseResumeDisabledMessage}</Text>
            </Alert>
          )}
          {hasDeployableContract && !solanaPauseUnavailable && solanaStateUnavailable && (
            <Alert status={isSolanaGovernanceStateError ? 'error' : 'info'} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{pauseResumeDisabledMessage}</Text>
            </Alert>
          )}
          {hasDeployableContract && solanaProgramPaused && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{pauseResumeDisabledMessage}</Text>
            </Alert>
          )}
          {solanaGovernanceState && (
            <Text fontSize="xs" color="gray.500">
              {t('orders.lifecycle.governance.onchainObservation', {
                sequence: solanaGovernanceState.pauseSequence,
                slot: solanaGovernanceState.orderStateSlot ?? solanaGovernanceState.platformConfigSlot,
              })}
            </Text>
          )}
          <HStack spacing={3} flexWrap="wrap">
            <Tooltip
              label={
                hasDeployableContract
                  && !solanaPauseUnavailable
                  && !solanaStateUnavailable
                  && !solanaProgramPaused
                  && !hasPendingSolanaAction
                  ? t('orders.lifecycle.governance.tooltips.pause') ?? undefined
                  : pauseResumeDisabledMessage ?? undefined
              }
            >
              <Button
                colorScheme="orange"
                size="sm"
                onClick={() => onRequestAction('emergency_pause')}
                isLoading={activeGovernanceAction === 'emergency_pause'}
                isDisabled={
                  !canPause ||
                  isLoading ||
                  (activeGovernanceAction !== null && activeGovernanceAction !== 'emergency_pause')
                }
              >
                {t('orders.lifecycle.governance.actions.pause')}
              </Button>
            </Tooltip>
            <Tooltip
              label={
                hasDeployableContract
                  && !solanaPauseUnavailable
                  && !solanaStateUnavailable
                  && !solanaProgramPaused
                  && !hasPendingSolanaAction
                  ? t('orders.lifecycle.governance.tooltips.resume') ?? undefined
                  : pauseResumeDisabledMessage ?? undefined
              }
            >
              <Button
                colorScheme="green"
                size="sm"
                onClick={() => onRequestAction('emergency_resume')}
                isLoading={activeGovernanceAction === 'emergency_resume'}
                isDisabled={
                  !canResume ||
                  isLoading ||
                  (activeGovernanceAction !== null && activeGovernanceAction !== 'emergency_resume')
                }
              >
                {t('orders.lifecycle.governance.actions.resume')}
              </Button>
            </Tooltip>
            <Tooltip
              label={
                hasDeployableContract
                  ? t('orders.lifecycle.governance.tooltips.close') ?? undefined
                  : governanceDisabledMessage ?? undefined
              }
            >
              <Button
                colorScheme="red"
                size="sm"
                onClick={() => onRequestAction('close_contract')}
                isLoading={activeGovernanceAction === 'close_contract'}
                isDisabled={
                  !hasDeployableContract ||
                  !canClose ||
                  isLoading ||
                  (activeGovernanceAction !== null && activeGovernanceAction !== 'close_contract')
                }
              >
                {t('orders.lifecycle.governance.actions.close')}
              </Button>
            </Tooltip>
          </HStack>
        </VStack>
      </Box>

      {sorted.length === 0 ? (
        <Text fontSize="sm" color="gray.600">
          {t('orders.lifecycle.governance.empty')}
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>{t('orders.lifecycle.governance.table.action')}</Th>
                <Th>{t('orders.lifecycle.governance.table.triggerSource')}</Th>
                <Th>{t('orders.lifecycle.governance.table.triggeredBy')}</Th>
                <Th>{t('orders.lifecycle.governance.table.notes')}</Th>
                <Th>{t('orders.lifecycle.governance.table.queueMode')}</Th>
                <Th>{t('orders.lifecycle.governance.table.txHash')}</Th>
                {isSolana ? (
                  <Th>{t('orders.lifecycle.addresses', 'Addresses')}</Th>
                ) : (
                  <Th>{t('orders.lifecycle.governance.table.contract')}</Th>
                )}
                <Th>{t('orders.lifecycle.governance.table.createdAt')}</Th>
                <Th>{t('orders.lifecycle.governance.table.updatedAt')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sorted.map(event => {
                const executionStatus = getSolanaExecutionStatus(event)
                const solanaAddresses = isSolana
                  ? resolveSolanaGovernanceAddressRoles(event, lifecycle)
                  : undefined
                const addressRows = solanaAddresses
                  ? [
                      {
                        label: t('orders.lifecycle.paymentAddress', 'Payment Address'),
                        value: solanaAddresses.paymentAddress,
                      },
                      {
                        label: t('orders.lifecycle.vaultTokenAccount', 'Vault Token Account'),
                        value: solanaAddresses.vaultTokenAccount,
                      },
                      {
                        label: t('orders.lifecycle.orderStateAddress', 'Order State Address'),
                        value: solanaAddresses.orderStateAddress,
                      },
                      {
                        label: t('orders.lifecycle.programAddress', 'Program Address'),
                        value: solanaAddresses.programAddress,
                      },
                    ]
                  : []
                return (
                  <Tr key={event.id}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Badge colorScheme={getGovernanceActionBadgeColor(event.action)}>
                        {getGovernanceActionLabel(event.action as GovernanceAction)}
                      </Badge>
                      {executionStatus && (
                        <Badge
                          variant="outline"
                          colorScheme={getExecutionStatusColor(executionStatus)}
                        >
                          {executionStatus}
                        </Badge>
                      )}
                    </VStack>
                  </Td>
                  <Td>{event.triggerSource ?? t('common.na')}</Td>
                  <Td>
                    {event.triggeredByAdminId ? (
                      <HStack spacing={1}>
                        <Code fontSize="xs">{event.triggeredByAdminId}</Code>
                        <CopyIconButton value={event.triggeredByAdminId} label={t('common.copy')} />
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {t('common.na')}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {event.notes ? (
                      <Text fontSize="sm">{event.notes}</Text>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {t('common.na')}
                      </Text>
                    )}
                  </Td>
                  <Td>{event.queueMode ?? t('common.na')}</Td>
                  <Td>
                    {event.txHash ? (
                      <HStack spacing={1}>
                        <Code fontSize="xs">{event.txHash}</Code>
                        <CopyIconButton value={event.txHash} label={t('common.copy')} />
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {t('common.na')}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {isSolana ? (
                      <VStack align="stretch" spacing={2} minW="220px">
                        {addressRows.map(item => (
                          <Box key={item.label}>
                            <Text fontSize="xs" color="gray.500">
                              {item.label}
                            </Text>
                            {item.value ? (
                              <HStack spacing={1}>
                                <Code fontSize="xs" wordBreak="break-all">
                                  {item.value}
                                </Code>
                                <CopyIconButton value={item.value} label={t('common.copy')} />
                              </HStack>
                            ) : (
                              <Text fontSize="sm" color="gray.500">
                                {t('common.na')}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    ) : event.contractAddress ? (
                      <HStack spacing={1}>
                        <Code fontSize="xs">{event.contractAddress}</Code>
                        <CopyIconButton value={event.contractAddress} label={t('common.copy')} />
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {t('common.na')}
                      </Text>
                    )}
                  </Td>
                  <Td>{formatTimestamp(event.createdAt, locale) ?? t('common.na')}</Td>
                  <Td>{formatTimestamp(event.updatedAt, locale) ?? t('common.na')}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  )
}
